<template>
  <div class="login-container">
    <!-- 七宝智控标志区域 - 移到左上角 -->
    <div class="brand-logos">
      <img src="@/assets/7d-logo.png" alt="7D Logo" class="brand-logo" />
      <img src="@/assets/agent-ai.png" alt="Agent AI" class="brand-logo" />
    </div>
    
    <!-- 登录内容区域 -->
    <div class="login-content">
      <!-- 左侧区域 - 机器人图片 -->
      <div class="login-left">
        <div class="robot-image">
          <img src="@/assets/login/login-person.png" alt="AI Robot" class="robot" />
        </div>
      </div>
      
      <!-- 右侧区域 - 登录表单 -->
      <div class="login-right">
        <div class="login-box">
          <div class="login-header">
            <h2>{{ $t('auth.loginTitle') }}</h2>
            <p>{{ $t('auth.loginSubtitle') }}</p>
          </div>
          
          <el-form
            ref="loginForm"
            :model="loginForm"
            :rules="loginRules"
            label-position="top"
            class="login-form"
          >
            <el-form-item prop="username">
              <el-input
                v-model="loginForm.username"
                :placeholder="$t('auth.accountPlaceholder')"
                prefix-icon="el-icon-user"
                clearable
                @keyup.enter.native="handleLogin"
              ></el-input>
            </el-form-item>
            
            <el-form-item prop="password">
              <el-input
                v-model="loginForm.password"
                type="password"
                :placeholder="$t('auth.passwordPlaceholder')"
                prefix-icon="el-icon-lock"
                show-password
                @keyup.enter.native="handleLogin"
              ></el-input>
            </el-form-item>
            
            <!-- 验证码输入框和图片显示 -->
            <el-form-item prop="captcha">
              <div class="captcha-container">
                <el-input
                  v-model="loginForm.captcha"
                  :placeholder="$t('auth.captchaPlaceholder')"
                  prefix-icon="el-icon-picture-outline"
                  class="captcha-input"
                  @keyup.enter.native="handleLogin"
                ></el-input>
                <div class="captcha-image">
                  <img 
                    v-if="captchaImage" 
                    :src="captchaImage" 
                    :alt="$t('auth.captcha')" 
                    class="captcha-img"
                    @click="refreshCaptcha"
                    :title="$t('auth.refreshCaptchaTooltip')"
                  />
                  <div v-else class="captcha-placeholder" @click="refreshCaptcha">
                    <i class="el-icon-refresh"></i>
                    <span>{{ $t('auth.getCaptcha') }}</span>
                  </div>
                </div>
              </div>
            </el-form-item>
            
            <div class="login-form-actions">
              <el-checkbox v-model="loginForm.rememberMe">{{ $t('auth.rememberMe') }}</el-checkbox>
              <router-link to="/login/forgot-password" class="forgot-password">
                {{ $t('auth.forgotPassword') }}
              </router-link>
            </div>
            
            <el-form-item>
              <el-button
                type="primary"
                size="large"
                class="login-btn"
                :loading="loading"
                @click="handleLogin"
              >
                {{ $t('auth.loginButton') }}
              </el-button>
            </el-form-item>
          </el-form>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import { getUUID } from '@/utils'
import { login, getCaptcha } from '@/apis/auth/auth'
import { sm2EncryptPassword } from '@/utils/auth'
import { Sm2Crypto } from '@/utils/sm2'

export default {
  name: 'Login',
  data() {
    return {
      loginForm: {
        username: '',
        password: '',
        captcha: '',
        rememberMe: false
      },
      captchaImage: '',
      captchaId: '',
      loading: false,
      loginRules: {
        username: [
          { required: true, message: this.$t('auth.accountRequired'), trigger: 'blur' }
        ],
        password: [
          { required: true, message: this.$t('auth.passwordRequired'), trigger: 'blur' }
        ],
        captcha: [
          { required: true, message: this.$t('auth.captchaRequired'), trigger: 'blur' },
          { min: 5, max: 5, message: this.$t('auth.captchaLength'), trigger: 'blur' }
        ]
      }
    }
  },
  mounted() {
    // 从localStorage获取记住的用户名
    const rememberedUsername = localStorage.getItem('rememberedUsername')
    if (rememberedUsername) {
      this.loginForm.username = rememberedUsername
      this.loginForm.rememberMe = true
    }
    
    // 初始化验证码
    this.refreshCaptcha()
  },
  methods: {
    // 自动识别输入类型：手机号/邮箱/用户名
    detectLoginType(input) {
      if (!input) return 'username'
      const trimmed = input.trim()
      // 11位数字且以1开头 → 手机号
      if (/^1[3-9]\d{9}$/.test(trimmed)) return 'phone'
      // 包含@和域名后缀 → 邮箱
      if (/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(trimmed)) return 'email'
      // 其他 → 用户名
      return 'username'
    },

    // 处理登录
    async handleLogin() {
      // 表单验证
      try {
        await this.$refs.loginForm.validate();
      } catch (error) {
        return;
      }

      this.loading = true
      
      try {
        // 如果勾选了记住我，保存用户名到localStorage
        if (this.loginForm.rememberMe && this.loginForm.username) {
          localStorage.setItem('rememberedUsername', this.loginForm.username)
        } else {
          localStorage.removeItem('rememberedUsername')
        }
        
        // 按照后端期望格式：验证码+密码组合后进行SM2加密
        let encryptedPassword;
        try {
          // 对验证码+密码组合进行SM2加密
          encryptedPassword = await Sm2Crypto.encryptPasswordWithCaptcha(this.loginForm.password, this.loginForm.captcha);
          console.log('SM2加密验证码+密码成功:', encryptedPassword);
        } catch (error) {
          console.error('SM2加密失败:', error);
          // 如果加密失败，抛出错误
          this.$message.error(this.$t('auth.encryptFailed'));
          return;
        }
        
        // 调用登录 action
        const loginType = this.detectLoginType(this.loginForm.username)
        const loginData = await this.$store.dispatch('user/login', {
          username: this.loginForm.username,
          password: encryptedPassword,  // 加密的验证码+密码组合
          captchaId: this.captchaId,     // 验证码ID
          loginType: loginType           // 自动识别：username/phone/email
        });
        
        console.log('登录成功:', loginData);
        this.$message.success(this.$t('auth.loginSuccess'));
        
        // 登录成功后跳转到首页或重定向页面
        const redirect = this.$route.query.redirect || '/dashboard';
        this.$router.replace(redirect).catch(err => {
          // 忽略由于导航守卫重定向导致的错误
          if (err.name !== 'NavigationDuplicated' && !err.message.includes('Redirected')) {
            console.error('路由跳转失败:', err);
          }
        });
      } catch (error) {
        console.error('登录错误:', error);
        // 登录失败时刷新验证码
        this.refreshCaptcha();
      } finally {
        this.loading = false;
      }
    },
    
    // 刷新验证码
    async refreshCaptcha() {
      try {
        // 生成新的UUID作为验证码ID
        this.captchaId = getUUID()
        
        // 获取验证码图片
        const response = await getCaptcha(this.captchaId)
        
        // 释放之前的URL对象以防内存泄漏
        if (this.captchaImage && this.captchaImage.startsWith('blob:')) {
          URL.revokeObjectURL(this.captchaImage)
        }
        
        this.captchaImage = URL.createObjectURL(response)
        
        // 清除当前输入的验证码
        this.loginForm.captcha = ''
        
        console.log('刷新验证码成功')
      } catch (error) {
        console.error('获取验证码失败:', error)
        // 尝试从 error 中获取更详细的信息
        const errorMsg = error.message || (error.getErrorMessage ? error.getErrorMessage() : '未知错误')
        this.$message.error(`获取验证码失败: ${errorMsg}`)
      }
    },
    
    // 移除内部生成UUID方法，改用工具类中的getUUID
  }
}
</script>

<style scoped>
.login-container {
  width: 100%;
  height: 100vh;
  background: linear-gradient(135deg, #667eea 0%, #764ba2 100%);
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  padding: 20px;
  position: relative;
}

/* 品牌logo区域 - 移到左上角 */
.brand-logos {
  position: absolute;
  top: 30px;
  left: 40px;
  display: flex;
  gap: 30px;
  align-items: center;
  z-index: 10;
}

.brand-logo {
  height: 50px;
  width: auto;
  object-fit: contain;
  filter: drop-shadow(0 2px 4px rgba(0, 0, 0, 0.1));
  transition: transform 0.3s ease;
}

.brand-logo:hover {
  transform: scale(1.05);
}

.login-content {
  display: flex;
  align-items: center;
  justify-content: center;
  width: 100%;
  margin-top: 80px;
  gap: 200px;
  padding: 0 40px;
}

.login-left {
  flex: 1;
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 30px 20px;
  max-width: 550px;
}

.robot-image {
  position: relative;
  text-align: center;
}

.robot {
  width: 420px;
  height: 420px;
  object-fit: contain;
  margin: 0 auto 30px;
  display: block;
  filter: drop-shadow(0 8px 16px rgba(0, 0, 0, 0.2));
  transition: transform 0.3s ease;
}

.robot:hover {
  transform: scale(1.02);
}

.login-right {
  flex: 1;
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 30px 20px;
  max-width: 550px;
}

.login-box {
  width: 100%;
  background: #fff;
  border-radius: 12px;
  box-shadow: 0 8px 32px rgba(0, 0, 0, 0.15);
  padding: 45px 35px;
  backdrop-filter: blur(10px);
  transition: box-shadow 0.3s ease;
}

.login-box:hover {
  box-shadow: 0 12px 40px rgba(0, 0, 0, 0.18);
}

.login-header {
  text-align: center;
  margin-bottom: 30px;
}

.login-header h2 {
  font-size: 28px;
  font-weight: 600;
  color: #333;
  margin-bottom: 8px;
}

.login-header p {
  font-size: 14px;
  color: #666;
}

.login-form {
  margin-bottom: 20px;
}

.captcha-container {
  display: flex;
  gap: 10px;
}

.captcha-image {
  flex-shrink: 0;
  width: 100px;
  height: 35px;
  cursor: pointer;
  overflow: hidden;
  border-radius: 4px;
  border: 1px solid #dcdfe6;
  padding: 2px;
}

.captcha-image img {
  width: 100%;
  height: 100%;
  object-fit: contain;
}

.captcha-placeholder {
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  height: 100%;
  color: #999;
  font-size: 12px;
  background: #f5f5f5;
  cursor: pointer;
  transition: background-color 0.3s ease;
}

.captcha-placeholder:hover {
  background: #e5e5e5;
}

.captcha-placeholder i {
  font-size: 14px;
  margin-bottom: 2px;
}

.captcha-input {
  flex: 1;
}

.login-form-actions {
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 25px;
}

.forgot-password {
  color: #667eea;
  text-decoration: none;
  font-size: 14px;
}

.forgot-password:hover {
  text-decoration: underline;
}

.login-btn {
  width: 100%;
  height: 45px;
  font-size: 16px;
  background: linear-gradient(135deg, #667eea 0%, #764ba2 100%);
  border: none;
  border-radius: 8px;
  box-shadow: 0 4px 12px rgba(102, 126, 234, 0.4);
  transition: all 0.3s ease;
}

.login-btn:hover {
  background: linear-gradient(135deg, #5a6fd8 0%, #6a4190 100%);
  box-shadow: 0 6px 16px rgba(102, 126, 234, 0.5);
  transform: translateY(-1px);
}

/* 响应式设计 */
@media (max-width: 768px) {
  .login-container {
    flex-direction: column;
    padding: 15px;
    justify-content: center;
    align-items: center;
  }
  
  .brand-logos {
    position: static;
    margin-bottom: 20px;
    gap: 20px;
    align-self: center;
  }
  
  .brand-logo {
    height: 40px;
  }
  
  .login-content {
    flex-direction: column;
    justify-content: center;
    align-items: center;
    margin-top: 20px;
    gap: 120px;
    padding: 0 20px;
    width: 100%;
  }
  
  .login-left,
  .login-right {
    flex: none;
    width: 100%;
    padding: 20px 15px;
    max-width: 400px;
    justify-content: center;
    align-items: center;
  }
  
  .robot-image {
    text-align: center;
    width: 100%;
  }
  
  .robot {
    width: 330px;
    height: 330px;
    margin: 0 auto 22px;
    display: block;
  }
  
  .login-box {
    padding: 30px 25px;
    max-width: 400px;
    margin: 0 auto;
  }
  
  .login-header h2 {
    font-size: 24px;
  }
}

@media (max-width: 480px) {
  .brand-logos {
    gap: 15px;
  }
  
  .brand-logo {
    height: 35px;
  }
  
  .login-content {
    gap: 90px;
    padding: 0 10px;
    justify-content: center;
    align-items: center;
  }
  
  .login-left,
  .login-right {
    padding: 15px 10px;
    justify-content: center;
    align-items: center;
    max-width: 350px;
  }
  
  .robot-image {
    text-align: center;
    width: 100%;
  }
  
  .robot {
    width: 330px;
    height: 330px;
    margin: 0 auto 22px;
    display: block;
  }
  
  .login-box {
    padding: 25px 20px;
    max-width: 350px;
    margin: 0 auto;
  }
  
  .captcha-container {
    flex-direction: column;
    gap: 10px;
  }
  
  .captcha-image {
    width: 100%;
  }
}
</style>










